// src/components/LandingPage.js
import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import HamburgerNav from './HamburgerNav';
import routePreloader from '../services/routePreloader';
import './LandingPage.css';

const LandingPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [activeFeaturePopup, setActiveFeaturePopup] = useState(null);

  // Feature boxes data (matching mobile app)
  const features = [
    {
      id: 1,
      icon: '🌍',
      title: 'Flexible Booking',
      text: 'Want to be somewhere off route? BackPass books you there at a highly subsidized, best possible rate.',
      hasLink: false
    },
    {
      id: 2,
      icon: '💰',
      title: 'Best Pricing Model',
      text: 'Price Certainty, Curated Routes, and Direct Bookings mean we can cut out the middlemen and pass the savings to you.',
      hasLink: false
    },
    {
      id: 3,
      icon: '🧘‍♀️',
      title: 'Peace of Mind',
      text: 'Immediate same day rebooking means you\'re never stuck somewhere you don\'t want to be. We\'re here every step of the way.',
      hasLink: false
    },
    {
      id: 4,
      icon: '🎒',
      title: 'By Us, For Us',
      text: 'BackPass was built because we wish we had this when we first started. ',
      linkText: 'Read our story here.',
      linkUrl: '/origin-story',
      hasLink: true
    }
  ];

  // Service cards data
  const serviceCards = [
    {
      id: 1,
      smiley: ':)',
      title: 'Take Your First Step',
      description: 'BackPass starts by recommending bookings along a curated route of best rated personally experienced hotels and hostels. Follow our lead or blaze your own path in our full service same-day booking engine. So..'
    },
    {
      id: 2,
      smiley: ':))',
      title: 'Get Ready',
      description: 'Our companion app lets you explore, plan, and connect with other travelers. It also comes loaded with tons of travel-tested tips to save you time and money (This alone will be worth the price). That being said, you probably spend enough time staring at screens already. With that in mind, BackPass is designed to get you to...'
    },
    {
      id: 3,
      smiley: ':)))',
      title: 'Go Outside',
      description: 'BackPass is all about being a part of something special. Once you\'re out there, dare to connect with the people and places that inspire you. We\'ll be here offering you flexibility and support the entire time, because...'
    },
    {
      id: 4,
      smiley: ':))))',
      title: 'We\'ve Got You',
      description: 'With flexibility, price certainty, and safety features other booking models simply can\'t compete with, BackPass is backpacking at its best. We open doors for people and make the world a better place, and we hope we\'ll inspire you to do the same.'
    }
  ];

  useEffect(() => {
    // Preload store routes data and images
    routePreloader.preloadRoutes()
      .then(() => console.log('Store routes preloaded successfully'))
      .catch(error => console.error('Error preloading routes:', error));

    // Intersection Observer for animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    // Observe elements
    const elements = document.querySelectorAll('.service-card');  // Removed .feature-item for now
    elements.forEach(el => {
      observer.observe(el);
    });

    // Parallax effect for decoration circles
    let ticking = false;
    function updateParallax() {
      const scrolled = window.pageYOffset;
      const circles = document.querySelectorAll('.decoration-circle');
      
      circles.forEach((circle, index) => {
        const speed = index === 0 ? 0.5 : 0.3;
        circle.style.transform = `translateY(${scrolled * speed}px)`;
      });
      
      ticking = false;
    }

    function requestTick() {
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }

    window.addEventListener('scroll', requestTick);

    // Generate stars for features section
    function generateStars() {
      const starsContainer = document.getElementById('features-stars');
      if (!starsContainer) return;

      const numberOfStars = 100;

      for (let i = 0; i < numberOfStars; i++) {
        const star = document.createElement('div');
        star.className = 'star';
        star.style.left = `${Math.random() * 100}%`;
        star.style.top = `${Math.random() * 100}%`;
        star.style.animationDelay = `${Math.random() * 3}s`;
        star.style.animationDuration = `${3 + Math.random() * 2}s`;

        // Vary star sizes
        if (Math.random() > 0.8) {
          star.style.width = '3px';
          star.style.height = '3px';
        }

        starsContainer.appendChild(star);
      }
    }

    generateStars();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', requestTick);
      elements.forEach(el => {
        observer.unobserve(el);
      });
    };
  }, []);

  // Preload all store route images after homepage loads
  useEffect(() => {
    let isSubscribed = true;

    async function startPreloading() {
      // Add additional delay to ensure homepage is fully rendered
      await new Promise(resolve => setTimeout(resolve, 1000));

      if (!isSubscribed) return;

      try {
        const routesSnap = await getDocs(collection(db, 'routes'));
        const routesData = routesSnap.docs.map((docSnap) => ({
          ...docSnap.data(),
          id: docSnap.id,
        }));

        // Preload ALL route images that have storeCoverImage
        let preloadedCount = 0;
        routesData.forEach(route => {
          if (route.storeCoverImage && route.storeStatus && route.storeStatus !== 'hidden') {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = route.storeCoverImage;
            document.head.appendChild(link);
            preloadedCount++;
          }
        });

        console.log(`✅ Preloading ${preloadedCount} route images from homepage`);
      } catch (error) {
        console.error('Error preloading store images:', error);
      }
    }

    // Wait for page to fully load before preloading store images
    if (document.readyState === 'complete') {
      startPreloading();
    } else {
      window.addEventListener('load', startPreloading);
    }

    return () => {
      isSubscribed = false;
      window.removeEventListener('load', startPreloading);
    };
  }, []);

  // Preload background images for hamburger menu pages
  useEffect(() => {
    let isSubscribed = true;

    async function preloadBackgroundImages() {
      // Wait for landing page to fully render first
      await new Promise(resolve => setTimeout(resolve, 1500));

      if (!isSubscribed) return;

      // Background images from all hamburger menu pages
      const backgroundImages = [
        '/images/servicesection.webp',      // StorePage (/go)
        '/images/hatgirl.webp',             // LearnMore hero
        '/images/mainbackground.jpg',       // LearnMore services section
        '/images/BackPassBackgroundGuy.webp', // LearnMore CTA + OriginStory
        '/images/archedbackground.webp'     // BackThePass
      ];

      backgroundImages.forEach(imagePath => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = imagePath;
        document.head.appendChild(link);
      });

      console.log(`✅ Preloaded ${backgroundImages.length} background images for navigation pages`);
    }

    // Wait for page to fully load before preloading
    if (document.readyState === 'complete') {
      preloadBackgroundImages();
    } else {
      window.addEventListener('load', preloadBackgroundImages);
    }

    return () => {
      isSubscribed = false;
      window.removeEventListener('load', preloadBackgroundImages);
    };
  }, []);

  // Detect if it's a mobile device for background attachment
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || 
                   (window.matchMedia && window.matchMedia('(hover: none)').matches);

  // Handle carousel touch events
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentSlide < serviceCards.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
    if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }

    // Reset touch values
    setTouchStart(0);
    setTouchEnd(0);
  };

  // Handle feature box clicks
  const openFeaturePopup = (feature) => {
    setActiveFeaturePopup(feature);
  };

  const closeFeaturePopup = () => {
    setActiveFeaturePopup(null);
  };

  return (
    <div className="landing-page-container">
      {/* Hero Section */}
      <section className="hero-section">
        {/* Hero Background Image */}
        <img
          src="/images/backpackers-group.webp"
          alt="Backpackers"
          className="hero-background-image"
        />
        {/* Gradient Overlay */}
        <div className="hero-gradient-overlay"></div>

        {/* Hamburger Navigation */}
        <HamburgerNav animationDelay="0.4s" />

        <div className="hero-decoration-circle"></div>
        <div className="hero-content">
          {/* Logo inside hero content */}
          <a href="/go" className="hero-logo">
            <img src="/images/logodownsized.webp" alt="BackPass Logo" className="logo" />
          </a>
          <h1 className="hero-title">One Pass, One Price</h1>
          <a href="/go" className="hero-go-button">Go</a>
        </div>
        
        {/* Glittering Arrows */}
        <div className="scroll-arrows">
          <div className="arrow"></div>
          <div className="arrow"></div>
          <div className="arrow"></div>
        </div>
      </section>

      {/* Services Section */}
      <section
        className="services-section"
        style={{
          backgroundImage: `url('/images/servicesection.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: isMobile ? 'scroll' : 'fixed'
        }}
      >
        {/* Desktop View */}
        <div className="services-container desktop-view">
          {serviceCards.map((card, index) => (
            <div key={card.id} className="service-card" style={{ animationDelay: `${index * 0.1}s` }}>
              <span className="service-number">{card.smiley}</span>
              <h2 className="service-title">{card.title}</h2>
              <p className="service-description">{card.description}</p>
            </div>
          ))}
        </div>

        {/* Mobile Carousel - Simplified with inline styles */}
        <div className="services-container mobile-view">
          <div
            style={{
              width: '100%',
              overflow: 'hidden',
              position: 'relative',
              touchAction: 'pan-y',
              userSelect: 'none',
              WebkitUserSelect: 'none'
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}>
            <div
              style={{
                display: 'flex',
                transform: `translateX(-${currentSlide * 100}%)`,
                transition: 'transform 0.3s ease'
              }}
            >
              {serviceCards.map((card, index) => (
                <div key={card.id} style={{
                  minWidth: '100%',
                  width: '100%',
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '40px 20px',
                  boxSizing: 'border-box'
                }}>
                  <div style={{
                    background: 'rgba(7, 34, 49, 0.85)',
                    borderRadius: '20px',
                    padding: '30px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid #FFD700',
                    maxWidth: '90%',
                    width: '100%'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '20px'
                    }}>
                      <h2 style={{
                        fontSize: '36px',
                        color: '#FFD700',
                        fontWeight: '400',
                        fontFamily: "'Caveat', cursive",
                        flex: 1,
                        textAlign: 'center',
                        paddingLeft: '10px',
                        paddingRight: '10px',
                        margin: 0
                      }}>{card.title}</h2>
                      <span style={{
                        fontSize: '32px',
                        color: '#FFD700',
                        fontFamily: "'Caveat', cursive",
                        minWidth: '40px',
                        textAlign: 'right'
                      }}>{card.smiley}</span>
                    </div>
                    <p style={{
                      fontSize: '16px',
                      color: '#FFFFFF',
                      lineHeight: '23px',
                      textAlign: 'center'
                    }}>{card.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dot Indicators */}
          <div className="carousel-dots">
            {serviceCards.map((_, index) => (
              <button
                key={index}
                className={`dot ${currentSlide === index ? 'active' : ''}`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - All four cards with visible class and stars */}
      <section style={{ padding: '80px 20px', background: '#072231', position: 'relative', overflow: 'hidden' }}>
        <div className="stars" id="features-stars"></div>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 10 }}>
          <h2 className="features-mobile-hint">Push These Lil Guys</h2>
          <div className="features-grid">
            {features.map((feature) => (
              <div
                key={feature.id}
                className="feature-item visible clickable"
                onClick={() => openFeaturePopup(feature)}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Popup */}
      {activeFeaturePopup && (
        <div className={`feature-popup-overlay ${activeFeaturePopup ? 'active' : ''}`} onClick={closeFeaturePopup}>
          <div className="feature-popup" onClick={(e) => e.stopPropagation()}>
            <button className="feature-popup-close" onClick={closeFeaturePopup}>X</button>
            <div className="feature-popup-icon">{activeFeaturePopup.icon}</div>
            <h3 className="feature-popup-title">{activeFeaturePopup.title}</h3>
            <p className="feature-popup-text">
              {activeFeaturePopup.text}
              {activeFeaturePopup.hasLink && (
                <a href={activeFeaturePopup.linkUrl} className="feature-popup-link">
                  {activeFeaturePopup.linkText}
                </a>
              )}
            </p>
          </div>
        </div>
      )}

      {/* CTA Section - Now with features background */}
      <section
        className="cta-section"
        style={{
          backgroundImage: `url('/images/whybackpass.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: isMobile ? 'scroll' : 'fixed',
          backgroundColor: 'transparent'
        }}
      >
        <div className="cta-content">
          <a href="/go" className="cta-go-button">Go</a>
          
          <div className="cta-polaroids">
            <a href="/learn-more" className="cta-polaroid cta-polaroid-left">
              <img src="/images/oliviamakesafriend.webp" alt="Learn More" className="cta-polaroid-image" />
              <div className="cta-polaroid-caption">Learn More</div>
            </a>
            
            <a href="/back-the-pass" className="cta-polaroid cta-polaroid-right">
              <img src="/images/harvestfestival.webp" alt="Back the Pass" className="cta-polaroid-image" />
              <div className="cta-polaroid-caption">Back the Pass</div>
            </a>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-links">
            <a href="/terms" className="footer-link">Terms & Conditions</a>
            <span className="footer-separator">|</span>
            <a href="/privacy" className="footer-link">Privacy Policy</a>
            <span className="footer-separator">|</span>
            <a href="mailto:matthew@backpass.co" className="footer-link">Contact</a>
          </div>
          <div className="footer-copyright">
            <p>2025 BackPass LLC. All rights reserved.</p>
            <p className="footer-tagline">If we do this right, the world will be a better place.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;